// Integração com Open Balena: autenticação, localização de dispositivos, status e reinício.
// Duas instâncias/frotas independentes compartilham usuário/senha (ver .env.example).

import { apiUrl } from "./api";

const OPEN_BALENA_INSTANCES = [
  {
    instance: "2",
    apiUrl: process.env.VUE_APP_OPEN_BALENA_API_URL2 || "",
    postgrestUrl: process.env.VUE_APP_OPEN_BALENA_POSTGREST_URL2 || "",
    // Instância 2 não tem painel admin próprio; usa o mesmo da instância 1.
    adminUrl: process.env.VUE_APP_OPEN_BALENA_UI_URL || "",
  },
  {
    instance: "1",
    apiUrl: process.env.VUE_APP_OPEN_BALENA_API_URL || "",
    postgrestUrl: process.env.VUE_APP_OPEN_BALENA_POSTGREST_URL || "",
    adminUrl: process.env.VUE_APP_OPEN_BALENA_UI_URL || "",
  },
];

const USERNAME = process.env.VUE_APP_OPEN_BALENA_USERNAME || "";
const PASSWORD = process.env.VUE_APP_OPEN_BALENA_PASSWORD || "";

const TOKEN_TTL_MS = 55 * 60 * 1000;
const LOOKUP_TTL_MS = 2 * 60 * 1000;

const tokenCache = new Map(); // instance -> { token, expiresAt }
const lookupCache = new Map(); // sensorId -> { ts, result }
const inflightLookups = new Map(); // sensorId -> Promise

export function isOpenBalenaConfigured() {
  return Boolean(USERNAME && PASSWORD);
}

function clearToken(instance) {
  tokenCache.delete(instance);
}

async function login(config) {
  const cached = tokenCache.get(config.instance);
  if (cached && cached.expiresAt > Date.now()) {
    return cached.token;
  }

  const response = await fetch(`${config.apiUrl}/login_`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ username: USERNAME, password: PASSWORD }),
  });

  if (!response.ok) {
    throw new Error(`Falha ao autenticar no Open Balena (instância ${config.instance})`);
  }

  const token = (await response.text()).trim();
  tokenCache.set(config.instance, { token, expiresAt: Date.now() + TOKEN_TTL_MS });
  return token;
}

async function authorizedFetch(config, path, options = {}) {
  const token = await login(config);
  const response = await fetch(`${config.apiUrl}${path}`, {
    ...options,
    headers: {
      ...(options.headers || {}),
      Authorization: `Bearer ${token}`,
    },
  });

  if (response.status === 401 || response.status === 403) {
    clearToken(config.instance);
  }

  return response;
}

async function postgrestFetch(config, path) {
  const token = await login(config);
  const response = await fetch(`${config.postgrestUrl}${path}`, {
    headers: { Authorization: `Bearer ${token}` },
  });

  if (response.status === 401 || response.status === 403) {
    clearToken(config.instance);
  }

  if (!response.ok) return [];
  try {
    return await response.json();
  } catch (e) {
    return [];
  }
}

const UUID_REGEX = /[0-9a-fA-F]{32}|[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}/;

function extractUuidFromText(text) {
  if (!text) return null;
  const match = String(text).match(UUID_REGEX);
  return match ? match[0] : null;
}

// Sensores vêm de várias telas com nomes de campo diferentes; tentamos as variações conhecidas.
function normalizeSensorForBalena(sensor) {
  if (!sensor) return { id: null, uuid: null, mac: null, tagId: null, name: null };

  const uuid =
    sensor.uuid ||
    extractUuidFromText(sensor.notes) ||
    extractUuidFromText(sensor.tag_id) ||
    null;

  const mac =
    sensor.mac ||
    sensor.mac_address ||
    sensor.sensor_mac ||
    sensor.device_mac ||
    sensor.dev_eui ||
    sensor.deveui ||
    sensor.lora_deveui ||
    sensor.lorawan_dev_eui ||
    sensor.hardware_eui ||
    sensor.ieee_address ||
    null;

  const name = sensor.name || sensor.sensor_name || sensor.device_name || null;
  const tagId = sensor.tag_id || null;

  return {
    id: sensor.pk ?? sensor.sensor ?? sensor.id ?? null,
    uuid,
    mac,
    tagId,
    name,
  };
}

const sensorApiCache = new Map(); // pk -> raw sensor payload da API principal

// Nem toda tela expõe o mac do sensor no objeto que já tem em mãos (ex.: as
// leituras de SmartFeed só trazem o pk do sensor). Nesses casos, buscamos o
// registro completo do sensor na API principal (`/sensors/{pk}/`) para obter
// o mac e usá-lo na correspondência com o Open Balena.
async function fetchSensorFromMainApi(pk, headers) {
  if (pk == null || !headers) return null;
  if (sensorApiCache.has(pk)) return sensorApiCache.get(pk);

  try {
    const response = await fetch(apiUrl(`sensors/${pk}/`), { headers });
    if (!response.ok) {
      sensorApiCache.set(pk, null);
      return null;
    }
    const data = await response.json();
    sensorApiCache.set(pk, data);
    return data;
  } catch (e) {
    return null;
  }
}

async function resolveSensorIdentity(sensor, headers) {
  const identity = normalizeSensorForBalena(sensor);
  if (identity.mac || identity.uuid) return identity;

  const apiSensor = await fetchSensorFromMainApi(identity.id, headers);
  if (!apiSensor) return identity;

  return normalizeSensorForBalena({ ...sensor, ...apiSensor });
}

function buildSearchTerms(identity) {
  const terms = new Set();
  if (identity.mac) {
    terms.add(identity.mac);
    terms.add(String(identity.mac).replace(/:/g, ""));
  }
  if (identity.tagId) terms.add(identity.tagId);
  if (identity.name) terms.add(identity.name);
  return Array.from(terms).filter(Boolean);
}

async function findDeviceByUuid(config, uuid) {
  const rows = await postgrestFetch(config, `/device?uuid=eq.${encodeURIComponent(uuid)}`);
  return rows[0] || null;
}

// O schema PostgREST do Open Balena usa nomes de tabela/coluna com espaço
// (ex.: "device environment variable", "device name"). Espaços precisam ir
// percent-encoded (%20) tanto no path quanto nas chaves de filtro.
const ENV_VAR_TABLES = ["device environment variable", "device service environment variable"];
const COL_DEVICE_NAME = encodeURIComponent("device name");
const COL_MAC_ADDRESS = encodeURIComponent("mac address");

async function findDeviceByEnvUuid(config, uuid) {
  const varNames = ["UUID_PLATFORM", "uuid_platform", "UUID", "uuid"];
  const nameFilter = varNames.map((n) => `name.eq.${encodeURIComponent(n)}`).join(",");

  for (const table of ENV_VAR_TABLES) {
    const rows = await postgrestFetch(
      config,
      `/${encodeURIComponent(table)}?or=(${nameFilter})&value=eq.${encodeURIComponent(uuid)}`
    );
    for (const row of rows) {
      const deviceId = row.device ?? row.device_id;
      if (!deviceId) continue;
      const devices = await postgrestFetch(config, `/device?id=eq.${deviceId}`);
      if (devices[0]) return devices[0];
    }
  }
  return null;
}

async function fuzzySearchDevices(config, terms) {
  const candidates = new Map();

  for (const term of terms) {
    const encoded = encodeURIComponent(`*${term}*`);

    const columnFilter = `or=(uuid.ilike.${encoded},${COL_DEVICE_NAME}.ilike.${encoded},${COL_MAC_ADDRESS}.ilike.${encoded},status.ilike.${encoded})`;
    const directRows = await postgrestFetch(config, `/device?${columnFilter}`);
    for (const row of directRows) candidates.set(row.id, row);

    for (const table of ENV_VAR_TABLES) {
      const rows = await postgrestFetch(config, `/${encodeURIComponent(table)}?value=ilike.${encoded}`);
      for (const row of rows) {
        const deviceId = row.device ?? row.device_id;
        if (!deviceId || candidates.has(deviceId)) continue;
        const devices = await postgrestFetch(config, `/device?id=eq.${deviceId}`);
        if (devices[0]) candidates.set(deviceId, devices[0]);
      }
    }
  }

  return Array.from(candidates.values());
}

function scoreCandidate(device, identity) {
  const haystacks = [device.uuid, device["device name"], device["mac address"]]
    .filter(Boolean)
    .map((v) => String(v).toLowerCase());

  let score = 0;
  for (const term of buildSearchTerms(identity)) {
    const needle = String(term).toLowerCase();
    if (haystacks.some((h) => h.includes(needle))) score += 1;
  }
  return score;
}

function pickBestDeviceMatch(devices, identity) {
  if (devices.length === 0) return null;
  if (identity.uuid) {
    const exact = devices.find((d) => d.uuid === identity.uuid);
    if (exact) return exact;
  }
  return devices
    .map((d) => ({ device: d, score: scoreCandidate(d, identity) }))
    .sort((a, b) => b.score - a.score)[0].device;
}

async function resolveDeviceCandidates(config, identity) {
  if (identity.uuid) {
    const byUuid = await findDeviceByUuid(config, identity.uuid);
    if (byUuid) return [byUuid];

    const byEnvUuid = await findDeviceByEnvUuid(config, identity.uuid);
    if (byEnvUuid) return [byEnvUuid];
  }

  // Nomes de sensor (ex.: "Silo 1") se repetem entre clientes diferentes na
  // mesma frota Balena — buscar só pelo nome arriscaria associar o card (e o
  // botão de reiniciar) ao dispositivo de outro cliente. Só tentamos a busca
  // fuzzy quando há pelo menos um identificador de hardware (mac ou tag_id);
  // o nome entra apenas para desempatar entre candidatos já encontrados assim.
  if (!identity.mac && !identity.tagId) return [];

  const terms = buildSearchTerms(identity);
  if (terms.length === 0) return [];
  return fuzzySearchDevices(config, terms);
}

function parseVpnConnected(value) {
  if (typeof value === "boolean") return value;
  if (typeof value === "number") return value === 1;
  if (typeof value === "string") return value === "t" || value === "true" || value === "1";
  return false;
}

function mapDevice(raw, config) {
  return {
    id: raw.id,
    uuid: raw.uuid,
    name: raw["device name"],
    online: raw["api heartbeat state"] === "online",
    vpnConnected: parseVpnConnected(raw["is connected to vpn"]),
    status: raw.status,
    lastConnectivityEvent: raw["last connectivity event"],
    lastVpnEvent: raw["last vpn event"],
    instance: config.instance,
    apiUrl: config.apiUrl,
    adminUrl: buildDeviceAdminUrl(config, raw.id),
  };
}

function buildDeviceAdminUrl(config, deviceId) {
  const base = config.adminUrl || "";
  return `${base.replace(/\/$/, "")}/#/device/${deviceId}/show`;
}

export async function lookupOpenBalenaDevice(sensor, headers) {
  const sensorId = normalizeSensorForBalena(sensor).id;
  if (sensorId == null) return { found: false, device: null };

  const cached = lookupCache.get(sensorId);
  if (cached && cached.ts > Date.now() - LOOKUP_TTL_MS) {
    return cached.result;
  }

  const inflight = inflightLookups.get(sensorId);
  if (inflight) return inflight;

  const promise = (async () => {
    let result = { found: false, device: null };
    const identity = await resolveSensorIdentity(sensor, headers);

    for (const config of OPEN_BALENA_INSTANCES) {
      if (!config.apiUrl || !config.postgrestUrl) continue;
      try {
        const candidates = await resolveDeviceCandidates(config, identity);
        const best = pickBestDeviceMatch(candidates, identity);
        if (best) {
          result = { found: true, device: mapDevice(best, config) };
          break;
        }
      } catch (e) {
        // tenta a próxima instância
      }
    }

    lookupCache.set(sensorId, { ts: Date.now(), result });
    inflightLookups.delete(sensorId);
    return result;
  })();

  inflightLookups.set(sensorId, promise);
  return promise;
}

function configForInstance(instance) {
  return OPEN_BALENA_INSTANCES.find((c) => c.instance === instance);
}

export async function refreshOpenBalenaDeviceStatus(device) {
  const config = configForInstance(device.instance);
  if (!config) return device;

  const rows = await postgrestFetch(config, `/device?id=eq.${device.id}`);
  if (!rows[0]) return device;
  return mapDevice(rows[0], config);
}

export function getOpenBalenaConnectivity(device) {
  if (!device || !device.online) return "offline";
  if (!device.vpnConnected) return "warning";
  return "healthy";
}

export function canRebootOpenBalenaDevice(device) {
  return Boolean(device && device.online === true);
}

function parseSupervisorRebootResponse(text) {
  const trimmed = (text || "").trim();
  if (trimmed === '"OK"' || trimmed === "OK") {
    return { success: true, message: "Reinício iniciado com sucesso." };
  }
  try {
    const parsed = JSON.parse(trimmed);
    if (parsed && parsed.Data === "OK" && !parsed.Error) {
      return { success: true, message: "Reinício iniciado com sucesso." };
    }
    if (parsed && parsed.Error && /no online device/i.test(parsed.Error)) {
      return { success: false, message: "Sensor offline — reinício indisponível." };
    }
    if (parsed && parsed.Error) {
      return { success: false, message: parsed.Error };
    }
  } catch (e) {
    // resposta não é JSON, cai no fallback abaixo
  }
  return { success: false, message: trimmed || "Não foi possível reiniciar o sensor." };
}

function parseLogEntry(raw) {
  if (typeof raw === "string") {
    return { message: raw, timestamp: null, isStdErr: false };
  }
  const timestampMs = raw.timestamp ?? raw.createdAt ?? null;
  return {
    message: raw.message ?? "",
    timestamp: timestampMs != null ? new Date(timestampMs) : null,
    isStdErr: Boolean(raw.isStdErr),
    isSystem: Boolean(raw.isSystem),
    serviceId: raw.serviceId ?? null,
  };
}

function parseLogsResponse(text) {
  const trimmed = (text || "").trim();
  if (!trimmed) return [];

  try {
    const parsed = JSON.parse(trimmed);
    if (Array.isArray(parsed)) return parsed.map(parseLogEntry);
  } catch (e) {
    // não é um array JSON único — pode ser NDJSON (uma linha por entrada)
  }

  return trimmed
    .split("\n")
    .filter(Boolean)
    .map((line) => {
      try {
        return parseLogEntry(JSON.parse(line));
      } catch (e) {
        return parseLogEntry(line);
      }
    });
}

export async function fetchOpenBalenaDeviceLogs(device, { count = 300 } = {}) {
  const config = configForInstance(device.instance);
  if (!config) return [];

  const response = await authorizedFetch(config, `/device/v2/${device.uuid}/logs?count=${count}`);
  if (!response.ok) {
    throw new Error(`Falha ao buscar logs do dispositivo (status ${response.status})`);
  }

  const text = await response.text();
  return parseLogsResponse(text);
}

export async function rebootOpenBalenaDevice(device) {
  const config = configForInstance(device.instance);
  if (!config) return { success: false, message: "Instância do Open Balena não encontrada." };

  try {
    const response = await authorizedFetch(config, "/supervisor/v1/reboot", {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ uuid: device.uuid }),
    });

    if (response.status === 401 || response.status === 403) {
      return { success: false, message: "Sessão expirada — tente novamente." };
    }

    const text = await response.text();
    return parseSupervisorRebootResponse(text);
  } catch (e) {
    return { success: false, message: "Erro de rede ao tentar reiniciar o sensor." };
  }
}
